// ==============================================================================
// SERVIÇOS DE DADOS PÚBLICOS & PREENCHIMENTO INTELIGENTE (ZERO DIGITAÇÃO)
// BrasilAPI (Pública/Gratuita), ViaCEP (Redundância), Catálogo Técnico & Placas
// ==============================================================================

export interface AddressResult {
  cep: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  codigoIbge?: string;
  formattedAddress: string;
  success: boolean;
  error?: string;
}

export interface CompanyResult {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  situacaoCadastral: string;
  cnae: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  cep: string;
  codigoIbge?: string;
  phone?: string;
  email?: string;
  formattedAddress: string;
  success: boolean;
  error?: string;
}

export interface VehicleResult {
  plate: string;
  formattedPlate: string;
  brand: string;
  model: string;
  version?: string;
  modelYear: number;
  manufacturingYear: number;
  color: string;
  fuel: string;
  engine?: string;
  chassiFinal?: string;
  fipeValueEstimate?: number;
  isMocked?: boolean;
  success: boolean;
  error?: string;
}

export interface HvacModelSpec {
  brand: string;
  modelName: string;
  btus: number;
  gasType: 'R410A' | 'R32' | 'R22';
  factoryGasChargeGrams: number;
  voltage: '220V' | '127V' | 'Bivolt';
  liquidLinePipe: string;  // Ex: '1/4"'
  gasLinePipe: string;     // Ex: '3/8"' ou '1/2"'
  inverter: boolean;
}

// ──────────────────────────────────────────────────────────────────────────────
// 1. BUSCA DE CEP (BrasilAPI com Fallback para ViaCEP)
// ──────────────────────────────────────────────────────────────────────────────

export async function fetchAddressByCep(cep: string): Promise<AddressResult> {
  const cleanCep = cep.replace(/\D/g, '');

  if (cleanCep.length !== 8) {
    return {
      cep,
      street: '',
      neighborhood: '',
      city: '',
      state: '',
      formattedAddress: '',
      success: false,
      error: 'CEP deve conter exatamente 8 dígitos.'
    };
  }

  // Tentativa 1: BrasilAPI v2 (com coordenadas e dados enriquecidos)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);

    const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cleanCep}`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      const street = data.street || '';
      const neighborhood = data.neighborhood || '';
      const city = data.city || '';
      const state = data.state || '';
      const formatted = [street, neighborhood, `${city} - ${state}`].filter(Boolean).join(', ');

      let codigoIbge = '';
      if (typeof data.ibge === 'object' && data.ibge !== null) {
        codigoIbge = String(data.ibge.city || data.ibge.codigo || '');
      } else if (typeof data.ibge === 'string') {
        codigoIbge = data.ibge;
      } else if (data.city_ibge) {
        codigoIbge = String(data.city_ibge);
      }

      return {
        cep: `${cleanCep.slice(0, 5)}-${cleanCep.slice(5)}`,
        street,
        neighborhood,
        city,
        state,
        codigoIbge,
        formattedAddress: formatted,
        success: true
      };
    }
  } catch (err) {
    console.warn('BrasilAPI CEP indisponível, tentando ViaCEP...', err);
  }

  // Tentativa 2: Fallback transparente no ViaCEP
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
    if (response.ok) {
      const data = await response.json();
      if (!data.erro) {
        const street = data.logradouro || '';
        const neighborhood = data.bairro || '';
        const city = data.localidade || '';
        const state = data.uf || '';
        const formatted = [street, neighborhood, `${city} - ${state}`].filter(Boolean).join(', ');
        const codigoIbge = data.ibge ? String(data.ibge) : '';

        return {
          cep: `${cleanCep.slice(0, 5)}-${cleanCep.slice(5)}`,
          street,
          neighborhood,
          city,
          state,
          codigoIbge,
          formattedAddress: formatted,
          success: true
        };
      }
    }
  } catch (err) {
    console.error('Erro ao consultar ViaCEP:', err);
  }

  return {
    cep,
    street: '',
    neighborhood: '',
    city: '',
    state: '',
    formattedAddress: '',
    success: false,
    error: 'Não foi possível encontrar o endereço para o CEP informado.'
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// 2. BUSCA DE CNPJ NA RECEITA FEDERAL (BrasilAPI)
// ──────────────────────────────────────────────────────────────────────────────

export async function fetchCompanyByCnpj(cnpj: string): Promise<CompanyResult> {
  const cleanCnpj = cnpj.replace(/\D/g, '');

  if (cleanCnpj.length !== 14) {
    return {
      cnpj,
      razaoSocial: '',
      nomeFantasia: '',
      situacaoCadastral: '',
      cnae: '',
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      state: '',
      cep: '',
      formattedAddress: '',
      success: false,
      error: 'CNPJ deve conter 14 dígitos.'
    };
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();

      const razaoSocial = data.razao_social || '';
      const nomeFantasia = data.nome_fantasia || razaoSocial;
      const situacaoCadastral = data.descricao_situacao_cadastral || 'ATIVA';
      const cnae = data.cnae_fiscal_descricao || '';
      
      const logradouroTipo = data.descricao_tipo_de_logradouro ? `${data.descricao_tipo_de_logradouro} ` : '';
      const street = `${logradouroTipo}${data.logradouro || ''}`.trim();
      const number = data.numero || 'S/N';
      const complement = data.complemento || '';
      const neighborhood = data.bairro || '';
      const city = data.municipio || '';
      const state = data.uf || '';
      const cepFormatted = data.cep ? `${data.cep.slice(0, 5)}-${data.cep.slice(5)}` : '';

      const fullAddressParts = [
        street ? `${street}, ${number}${complement ? ` (${complement})` : ''}` : '',
        neighborhood,
        `${city} - ${state}`,
        cepFormatted ? `CEP ${cepFormatted}` : ''
      ].filter(Boolean);

      const formattedAddress = fullAddressParts.join(', ');

      let phone = data.ddd_telefone_1 || '';
      if (phone) {
        const cleanPhone = phone.replace(/\D/g, '');
        if (cleanPhone.length >= 10) {
          phone = `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(2, cleanPhone.length - 4)}-${cleanPhone.slice(cleanPhone.length - 4)}`;
        }
      }

      const codigoIbge = data.codigo_municipio_ibge
        ? String(data.codigo_municipio_ibge)
        : (data.codigo_municipio ? String(data.codigo_municipio) : '');

      return {
        cnpj: cleanCnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5'),
        razaoSocial,
        nomeFantasia,
        situacaoCadastral,
        cnae,
        street,
        number,
        complement,
        neighborhood,
        city,
        state,
        cep: cepFormatted,
        codigoIbge: codigoIbge || undefined,
        phone,
        email: data.email || undefined,
        formattedAddress,
        success: true
      };
    } else {
      const errorData = await response.json().catch(() => null);
      return {
        cnpj,
        razaoSocial: '',
        nomeFantasia: '',
        situacaoCadastral: '',
        cnae: '',
        street: '',
        number: '',
        neighborhood: '',
        city: '',
        state: '',
        cep: '',
        formattedAddress: '',
        success: false,
        error: errorData?.message || 'CNPJ não encontrado na base pública da Receita Federal.'
      };
    }
  } catch (err) {
    console.error('Erro ao consultar CNPJ na BrasilAPI:', err);
    return {
      cnpj,
      razaoSocial: '',
      nomeFantasia: '',
      situacaoCadastral: '',
      cnae: '',
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      state: '',
      cep: '',
      formattedAddress: '',
      success: false,
      error: 'Falha na conexão com a Receita Federal. Verifique sua internet.'
    };
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// 3. CONSULTA VEICULAR POR PLACA (Oficinas Mecânicas & Auto Centers)
// ──────────────────────────────────────────────────────────────────────────────

// Catálogo interno realista para resposta instantânea e simulação de alta precisão
const POPULAR_VEHICLES_CATALOG = [
  { brand: 'Volkswagen', model: 'Gol 1.6 MSI TotalFlex', version: 'Comfortline', engine: '1.6 8V EA111', fuel: 'Flex (Álcool/Gasolina)', fipe: 48500 },
  { brand: 'Volkswagen', model: 'Polo 1.0 TSI Turbo', version: 'Highline Automático', engine: '1.0 12V TSI', fuel: 'Flex', fipe: 89000 },
  { brand: 'Volkswagen', model: 'T-Cross 1.4 250 TSI', version: 'Highline', engine: '1.4 16V Turbo', fuel: 'Flex', fipe: 125000 },
  { brand: 'Volkswagen', model: 'Saveiro 1.6 MSI Cabine Dupla', version: 'Robust', engine: '1.6 16V', fuel: 'Flex', fipe: 67000 },
  { brand: 'Chevrolet', model: 'Onix 1.0 Turbo Flex', version: 'Premier Automático', engine: '1.0 12V Turbo Ecotec', fuel: 'Flex', fipe: 79900 },
  { brand: 'Chevrolet', model: 'Tracker 1.2 Turbo', version: 'Premier', engine: '1.2 12V Turbo', fuel: 'Flex', fipe: 118000 },
  { brand: 'Chevrolet', model: 'S10 2.8 CTDI 4x4', version: 'High Country Automática', engine: '2.8 Turbo Diesel Duramax', fuel: 'Diesel S10', fipe: 215000 },
  { brand: 'Fiat', model: 'Strada 1.3 Firefly Flex', version: 'Volcano Cabine Dupla', engine: '1.3 8V Firefly', fuel: 'Flex', fipe: 92000 },
  { brand: 'Fiat', model: 'Toro 1.3 Turbo 270', version: 'Volcano AT6', engine: '1.3 16V T270 GSE', fuel: 'Flex', fipe: 135000 },
  { brand: 'Fiat', model: 'Mobi 1.0 Fire', version: 'Like', engine: '1.0 8V Fire EVO', fuel: 'Flex', fipe: 54000 },
  { brand: 'Fiat', model: 'Palio 1.4 Fire Flex', version: 'Attractiveline', engine: '1.4 8V Fire', fuel: 'Flex', fipe: 36000 },
  { brand: 'Hyundai', model: 'HB20 1.0 TGDI Turbo', version: 'Platinum Plus', engine: '1.0 12V Kappa Turbo GDI', fuel: 'Flex', fipe: 86500 },
  { brand: 'Hyundai', model: 'Creta 1.0 TGDI Flex', version: 'Ultimate Automático', engine: '1.0 12V TGDI', fuel: 'Flex', fipe: 132000 },
  { brand: 'Toyota', model: 'Corolla 2.0 Dynamic Force Flex', version: 'Altis Premium Direct Shift', engine: '2.0 16V VVT-iE', fuel: 'Flex', fipe: 142000 },
  { brand: 'Toyota', model: 'Hilux CD 2.8 4x4 TDI', version: 'SRX Automática', engine: '2.8 16V D-4D Turbo Diesel', fuel: 'Diesel S10', fipe: 285000 },
  { brand: 'Honda', model: 'Civic 2.0 i-VTEC FlexOne', version: 'Touring / EXL', engine: '2.0 16V i-VTEC', fuel: 'Flex', fipe: 115000 },
  { brand: 'Honda', model: 'HR-V 1.5 Turbo Flex', version: 'Touring Automático', engine: '1.5 16V VTEC Turbo', fuel: 'Flex', fipe: 158000 },
  { brand: 'Jeep', model: 'Renegade 1.3 T270 Flex', version: 'Longitude AT6', engine: '1.3 16V Turbo GSE', fuel: 'Flex', fipe: 109000 },
  { brand: 'Jeep', model: 'Compass 1.3 T270 Flex', version: 'Limited AT6', engine: '1.3 16V T270', fuel: 'Flex', fipe: 165000 },
  { brand: 'Renault', model: 'Kwid 1.0 12V SCe', version: 'Zen / Intense', engine: '1.0 12V 3C SCe', fuel: 'Flex', fipe: 49000 },
  { brand: 'Nissan', model: 'Kicks 1.6 16V Flex', version: 'Exclusive CVT', engine: '1.6 16V HR16DE', fuel: 'Flex', fipe: 98000 },
  { brand: 'Ford', model: 'Ranger 3.2 TDCi 4x4', version: 'Limited CD Automática', engine: '3.2 Duratorq 5 Cilindros', fuel: 'Diesel', fipe: 198000 }
];

const COLORS_POOL = ['Prata', 'Branco Cristal', 'Preto Ninja', 'Cinza Chumbo', 'Vermelho Carmim', 'Azul Marinho', 'Verde Floresta'];

export async function fetchVehicleByPlate(plate: string): Promise<VehicleResult> {
  const cleanPlate = plate.toUpperCase().replace(/[^A-Z0-9]/g, '');

  // Validação: Formato antigo (AAA9999) ou Mercosul (AAA9A99)
  const isOldPattern = /^[A-Z]{3}[0-9]{4}$/.test(cleanPlate);
  const isMercosul = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(cleanPlate);

  if (!isOldPattern && !isMercosul) {
    return {
      plate,
      formattedPlate: plate,
      brand: '',
      model: '',
      modelYear: 0,
      manufacturingYear: 0,
      color: '',
      fuel: '',
      success: false,
      error: 'Formato de placa inválido. Digite no padrão Mercosul (ex: BRA2E19) ou convencional (ex: ABC-1234).'
    };
  }

  const formattedPlate = isMercosul 
    ? `${cleanPlate.slice(0, 3)}${cleanPlate.slice(3)}`
    : `${cleanPlate.slice(0, 3)}-${cleanPlate.slice(3)}`;

  // Se o usuário configurou uma chave de API oficial de placas no .env
  const apiKey = (import.meta as any).env?.VITE_PLACA_API_KEY;
  const apiEndpoint = (import.meta as any).env?.VITE_PLACA_API_ENDPOINT;

  if (apiKey && apiEndpoint) {
    try {
      const response = await fetch(`${apiEndpoint}?placa=${cleanPlate}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        const liveData = await response.json();
        return {
          plate: cleanPlate,
          formattedPlate,
          brand: liveData.marca || liveData.brand || 'Desconhecida',
          model: liveData.modelo || liveData.model || '',
          version: liveData.versao || liveData.subModelo,
          modelYear: liveData.anoModelo || liveData.ano,
          manufacturingYear: liveData.anoFabricacao || liveData.anoModelo - 1,
          color: liveData.cor || 'Prata',
          fuel: liveData.combustivel || 'Flex',
          engine: liveData.motor || liveData.cilindradas,
          chassiFinal: liveData.chassi ? `***${liveData.chassi.slice(-6)}` : undefined,
          fipeValueEstimate: liveData.valorFipe,
          isMocked: false,
          success: true
        };
      }
    } catch (err) {
      console.warn('Falha na API oficial de placas, utilizando motor nativo resiliente:', err);
    }
  }

  // Em ambiente de produção sem API oficial configurada, não injeta veículo sintético
  if (!import.meta.env.DEV) {
    return {
      plate: cleanPlate,
      formattedPlate,
      brand: '',
      model: '',
      modelYear: 0,
      manufacturingYear: 0,
      color: '',
      fuel: '',
      success: false,
      error: 'Consulta automática de placas indisponível no momento. Preencha os dados do veículo manualmente.'
    };
  }

  // Motor Nativo de Simulação em Desenvolvimento com Hash Determinístico (Mesma placa sempre retorna o mesmo carro)
  let hash = 0;
  for (let i = 0; i < cleanPlate.length; i++) {
    hash = (hash * 31 + cleanPlate.charCodeAt(i)) & 0xffffffff;
  }
  const positiveHash = Math.abs(hash);

  const vehicleTemplate = POPULAR_VEHICLES_CATALOG[positiveHash % POPULAR_VEHICLES_CATALOG.length];
  const color = COLORS_POOL[(positiveHash >> 3) % COLORS_POOL.length];
  
  // Ano entre 2017 e 2024
  const yearOffset = (positiveHash >> 5) % 8;
  const modelYear = 2017 + yearOffset;
  const manufacturingYear = modelYear > 2017 && ((positiveHash & 1) === 0) ? modelYear - 1 : modelYear;
  const chassiFinal = `***${(100000 + (positiveHash % 899999))}`;

  return {
    plate: cleanPlate,
    formattedPlate,
    brand: vehicleTemplate.brand,
    model: vehicleTemplate.model,
    version: vehicleTemplate.version,
    modelYear,
    manufacturingYear,
    color,
    fuel: vehicleTemplate.fuel,
    engine: vehicleTemplate.engine,
    chassiFinal,
    fipeValueEstimate: vehicleTemplate.fipe,
    isMocked: true,
    success: true
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// 4. CATÁLOGO TÉCNICO DE CLIMATIZAÇÃO (HVAC / AR-CONDICIONADO)
// ──────────────────────────────────────────────────────────────────────────────

export const HVAC_POPULAR_MODELS: HvacModelSpec[] = [
  // LG Dual Inverter
  { brand: 'LG', modelName: 'Dual Inverter Voice 9.000 BTUs', btus: 9000, gasType: 'R32', factoryGasChargeGrams: 580, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'LG', modelName: 'Dual Inverter Voice 12.000 BTUs', btus: 12000, gasType: 'R32', factoryGasChargeGrams: 670, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'LG', modelName: 'Dual Inverter Voice 18.000 BTUs', btus: 18000, gasType: 'R32', factoryGasChargeGrams: 1000, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '1/2"', inverter: true },
  { brand: 'LG', modelName: 'Dual Inverter Voice 24.000 BTUs', btus: 24000, gasType: 'R32', factoryGasChargeGrams: 1250, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '5/8"', inverter: true },
  
  // Midea / Springer Midea
  { brand: 'Midea', modelName: 'AirVolution Inverter 9.000 BTUs', btus: 9000, gasType: 'R410A', factoryGasChargeGrams: 550, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Midea', modelName: 'AirVolution Inverter 12.000 BTUs', btus: 12000, gasType: 'R410A', factoryGasChargeGrams: 650, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Midea', modelName: 'Inverter Xtreme Save Connect 18.000 BTUs', btus: 18000, gasType: 'R32', factoryGasChargeGrams: 880, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '1/2"', inverter: true },
  { brand: 'Springer Midea', modelName: 'Xtreme Save Inverter 12.000 BTUs', btus: 12000, gasType: 'R32', factoryGasChargeGrams: 600, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },

  // Samsung WindFree
  { brand: 'Samsung', modelName: 'WindFree Connect 9.000 BTUs', btus: 9000, gasType: 'R32', factoryGasChargeGrams: 650, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Samsung', modelName: 'WindFree Connect 12.000 BTUs', btus: 12000, gasType: 'R32', factoryGasChargeGrams: 700, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Samsung', modelName: 'WindFree PowerVolt 12.000 BTUs (Bivolt)', btus: 12000, gasType: 'R32', factoryGasChargeGrams: 700, voltage: 'Bivolt', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Samsung', modelName: 'WindFree Connect 18.000 BTUs', btus: 18000, gasType: 'R32', factoryGasChargeGrams: 1100, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '1/2"', inverter: true },

  // Daikin
  { brand: 'Daikin', modelName: 'EcoSwing Inverter 9.000 BTUs', btus: 9000, gasType: 'R32', factoryGasChargeGrams: 550, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Daikin', modelName: 'EcoSwing Inverter 12.000 BTUs', btus: 12000, gasType: 'R32', factoryGasChargeGrams: 620, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Daikin', modelName: 'EcoSwing Inverter 18.000 BTUs', btus: 18000, gasType: 'R32', factoryGasChargeGrams: 950, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '1/2"', inverter: true },

  // Gree
  { brand: 'Gree', modelName: 'G-Top Inverter 12.000 BTUs', btus: 12000, gasType: 'R410A', factoryGasChargeGrams: 750, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Gree', modelName: 'Eco Garden Convencional 12.000 BTUs', btus: 12000, gasType: 'R410A', factoryGasChargeGrams: 800, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '1/2"', inverter: false },

  // Elgin & Consul
  { brand: 'Elgin', modelName: 'Eco Inverter II 12.000 BTUs', btus: 12000, gasType: 'R32', factoryGasChargeGrams: 570, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '3/8"', inverter: true },
  { brand: 'Consul', modelName: 'Maxi Refrigeração 12.000 BTUs', btus: 12000, gasType: 'R410A', factoryGasChargeGrams: 700, voltage: '220V', liquidLinePipe: '1/4"', gasLinePipe: '1/2"', inverter: false }
];

export function searchHvacSpecs(query: string): HvacModelSpec[] {
  const clean = query.toLowerCase().trim();
  if (!clean) return HVAC_POPULAR_MODELS.slice(0, 8);

  return HVAC_POPULAR_MODELS.filter(item => 
    item.brand.toLowerCase().includes(clean) ||
    item.modelName.toLowerCase().includes(clean) ||
    item.btus.toString().includes(clean) ||
    item.gasType.toLowerCase().includes(clean)
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// 5. CÁLCULO PARA CORTE A LASER & CNC
// ──────────────────────────────────────────────────────────────────────────────

export interface LaserCalculationResult {
  perimeterMeters: number;
  areaSquareMeters: number;
  estimatedMinutes: number;
  recommendedPowerWatts: number;
  suggestedCuttingPrice: number;
}

export function calculateLaserCncMetrics(
  perimeterMeters: number,
  areaSquareMeters: number,
  materialType: 'mdf_3mm' | 'mdf_6mm' | 'acrilico_3mm' | 'acrilico_5mm' | 'compensado' | 'outro',
  vhbRatePerHour: number = 80
): LaserCalculationResult {
  // Velocidades médias de corte em metros por minuto
  const speeds: Record<string, { speedMMin: number; power: number; factor: number }> = {
    mdf_3mm: { speedMMin: 1.8, power: 80, factor: 1.0 },
    mdf_6mm: { speedMMin: 0.9, power: 100, factor: 1.4 },
    acrilico_3mm: { speedMMin: 1.2, power: 90, factor: 1.3 },
    acrilico_5mm: { speedMMin: 0.7, power: 110, factor: 1.7 },
    compensado: { speedMMin: 1.0, power: 90, factor: 1.2 },
    outro: { speedMMin: 1.0, power: 80, factor: 1.0 }
  };

  const config = speeds[materialType] || speeds.outro;
  
  // Tempo de corte puro (minutos) + tempo de setup / posicionamento (2 min)
  const cutMinutes = (perimeterMeters / Math.max(0.1, config.speedMMin)) + 2;
  const machineCost = (cutMinutes / 60) * vhbRatePerHour;
  const suggestedPrice = Math.max(25, Math.round(machineCost * 1.35));

  return {
    perimeterMeters,
    areaSquareMeters,
    estimatedMinutes: Math.ceil(cutMinutes),
    recommendedPowerWatts: config.power,
    suggestedCuttingPrice: suggestedPrice
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// 6. EXTRAÇÃO DE FORNECEDOR A PARTIR DE NOTA FISCAL (NFC-e / CHAVE DE ACESSO)
// ──────────────────────────────────────────────────────────────────────────────

export function extractCnpjFromAccessKey(accessKey: string): string | null {
  const clean = accessKey.replace(/\D/g, '');
  if (clean.length !== 44) return null;
  // Na chave de acesso padrão brasileira da NF-e / NFC-e:
  // Posição 0..1: UF (2)
  // Posição 2..5: AAMM (4)
  // Posição 6..19: CNPJ do emitente (14 dígitos)
  return clean.slice(6, 20);
}
